/**
 * Authentication Code Component
 * Komponent för tvåfaktorsautentisering med OTP-kod
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { verifyOtpRequest, resendOtpRequest, type OtpType } from '@/services/auth/authService';
import { useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';

type AuthenticationCodeType = {
  setIsVerified: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  type: OtpType;
};

const AuthenticationCode = ({ setIsVerified, email, type }: AuthenticationCodeType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLoginDetails } = useAuthActions();
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const maskedEmail = email?.replace(/(\w{1})(.*)(@.*)/, '$1****$3');

  useEffect(() => {
    const timer = timeLeft > 0 && setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    if (timeLeft === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft]);

  const handleResend = useCallback(async () => {
    try {
      setIsLoading(true);
      setTimeLeft(30);
      setCanResend(false);
      setError('');

      await resendOtpRequest({ email, type });
      toast.success(t('auth.otpResent') || 'Verifieringskod skickad igen');
    } catch (error: any) {
      console.error('OTP resend error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Kunde inte skicka ny kod';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [email, type, t]);

  const onVerifyOTP = useCallback(async () => {
    if (otp.length !== 4) {
      toast.error(t('auth.invalidOtp') || 'Ange en giltig 4-siffrig kod');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await verifyOtpRequest({
        email,
        otp,
        type,
      });

      if (response.data?.success && response.data.data) {
        // Type guard för AuthData
        const authData = response.data.data;
        if ('user' in authData && ('session' in authData || 'accessToken' in authData)) {
          setLoginDetails({
            user: authData.user,
          });
        }

        toast.success(t('auth.otpVerified') || 'Verifiering lyckades');
        navigate('/');
      }
    } catch (error: any) {
      console.error('OTP verify error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Verifiering misslyckades';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [otp, email, type, navigate, setLoginDetails, t]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-[28px] font-semibold tracking-tight text-[#1a2332]">
          {t('auth.otpTitle') || 'Verifieringskod'}
        </h2>
        <p className="mt-1.5 text-sm text-[#6b7280]">
          {t('auth.otpDescription') || 'Vi har skickat en verifieringskod till'} {maskedEmail}
        </p>
      </div>

      <div className="relative flex flex-col items-start space-y-4">
        <InputOTP
          maxLength={4}
          value={otp}
          onChange={(value) => {
            if (/^\d*$/.test(value)) {
              setOtp(value);
              setError('');
            }
          }}
          className="gap-2"
          onComplete={onVerifyOTP}
          disabled={isLoading}
        >
          <InputOTPGroup className="gap-2">
            {[...Array(4)].map((_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className={`rounded-lg border bg-white text-[#1c2430] ${
                  error ? 'border-red-500' : 'border-[#e3e7eb]'
                } transition-colors`}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex items-center gap-2 text-sm text-[#6b7280]">
          <span>{`00:${timeLeft < 10 ? `0${timeLeft}` : timeLeft}`}</span>
          <button
            type="button"
            className="font-medium text-[#14988e] hover:text-[#0d756c] disabled:cursor-not-allowed disabled:text-[#9aa3ad]"
            disabled={!canResend || isLoading}
            onClick={handleResend}
          >
            {t('auth.resend') || 'Skicka igen'}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setIsVerified(false)}
          disabled={isLoading}
          className="text-sm text-[#6b7280] hover:text-[#1a2332]"
        >
          {t('common.back') || 'Tillbaka'}
        </button>
      </div>
    </div>
  );
};

export default AuthenticationCode;

